import React, { useState } from 'react';
import { Link } from "react-router-dom";
import {Shield, Heart, Globe, Video, Calendar, Award} from 'lucide-react';
import BookingModal from '../components/BookingModal';

export default function About() {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-navy-900 via-navy-800 to-navy-900">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-400/10 via-transparent to-blue-500/10"></div>
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-400/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }}></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <h1 className="font-heading text-5xl md:text-7xl font-bold mb-8 bg-gradient-to-r from-cyan-400 via-blue-500 to-cyan-400 bg-clip-text text-transparent leading-tight">
              About Haven Travel Health
            </h1>
            <p className="font-sans text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Revolutionizing travel health through accessible, expert virtual consultations for British Columbians embarking on international adventures.
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 md:p-16 rounded-3xl border border-cyan-400/20 shadow-2xl">
              <h2 className="font-heading text-4xl md:text-5xl font-bold mb-8 text-white">Our Mission</h2>
              <p className="font-sans text-lg md:text-xl text-gray-200 leading-relaxed mb-8">
                Haven Travel Health was founded on the belief that expert travel medicine should be accessible to every British Columbia traveller, no matter where they live. We combine cutting-edge telehealth technology with specialized travel medicine expertise to deliver personalized, convenient care that fits your schedule.
              </p>
              <p className="font-sans text-lg md:text-xl text-gray-200 leading-relaxed">
                Our virtual-first model eliminates barriers like long wait times, limited clinic hours, and geographic constraints—while maintaining the highest standards of clinical excellence and personalized care.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-center mb-20 text-white">
              Meet Our Founder
            </h2>
            
            <div className="grid md:grid-cols-2 gap-16 items-start">
              {/* Photo */}
              <div className="relative group order-2 md:order-1">
                <div className="absolute inset-0 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-3xl blur-2xl opacity-40 group-hover:opacity-60 transition-all duration-500"></div>
                <div className="relative glass-card p-3 rounded-3xl overflow-hidden shadow-2xl">
                  <img 
                    src="https://static.lumi.new/ff/ffbe00037a62d561cf6fbb5e42316846.webp" 
                    alt="Dr. Saba Baig, PharmD, Founder of Haven Travel Health"
                    className="w-full h-auto rounded-2xl"
                  />
                </div>
              </div>

              {/* Bio */}
              <div className="space-y-7 order-1 md:order-2">
                <div>
                  <h3 className="font-heading text-4xl md:text-5xl font-bold text-white mb-3 leading-tight">
                    Dr. Saba Baig
                  </h3>
                  <p className="font-sans text-xl md:text-2xl text-cyan-400 font-semibold mb-6">
                    PharmD, Founder & Clinical Director
                  </p>
                </div>

                <div className="space-y-5 font-sans text-base md:text-lg text-gray-300 leading-relaxed">
                  <p>
                    Dr. Saba Baig's journey with Haven Travel Health began at the intersection of two profound passions: healthcare excellence and global exploration. As a practising pharmacist with specialized training in travel medicine, she witnessed firsthand the challenges British Columbia travellers faced in accessing timely, expert pre-travel health consultations.
                  </p>
                  
                  <p className="italic border-l-4 border-cyan-400 pl-6 py-2">
                    "I've always believed that travel transforms us—it broadens perspectives, creates lasting memories, and connects us to the world," Dr. Baig reflects. "But I also saw too many patients in BC struggling to find convenient, affordable travel health services, especially those living outside major urban centres or juggling demanding work schedules."
                  </p>

                  <p>
                    Drawing on her clinical expertise and personal travel experiences across four continents, Dr. Baig envisioned a better way: a virtual travel health clinic that could reach British Columbians wherever they are, offering the same personalized, evidence-based care traditionally only available through in-person visits.
                  </p>

                  <p>
                    Since founding Haven Travel Health, Dr. Baig has helped thousands of British Columbians prepare safely for international travel—from first-time backpackers exploring Southeast Asia to families embarking on safari adventures, and pilgrims preparing for Hajj. Her commitment to accessible, compassionate care continues to drive Haven's mission of making expert travel medicine available to every BC traveller.
                  </p>

                  <p className="text-cyan-400 font-semibold text-xl border-t border-cyan-400/30 pt-6 mt-6">
                    "Your journey deserves expert preparation. Let's make sure you travel with confidence and peace of mind."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-28 bg-navy-900/50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-center mb-20 text-white">
              Our Values
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 text-center group hover:scale-105 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:shadow-2xl group-hover:shadow-cyan-400/50 transition-all">
                  <Heart className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-5">Patient-Centered Care</h3>
                <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                  Every traveller receives personalized recommendations based on their unique health profile, itinerary, and travel style.
                </p>
              </div>

              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 text-center group hover:scale-105 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:shadow-2xl group-hover:shadow-cyan-400/50 transition-all">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-5">Clinical Excellence</h3>
                <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                  Evidence-based travel medicine guidance aligned with the latest international health guidelines and destination-specific risks.
                </p>
              </div>

              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 text-center group hover:scale-105 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-full flex items-center justify-center mx-auto mb-8 group-hover:shadow-2xl group-hover:shadow-cyan-400/50 transition-all">
                  <Globe className="w-10 h-10 text-white" />
                </div>
                <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-5">Accessibility</h3>
                <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                  Breaking down barriers to travel health services through virtual consultations available across British Columbia.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We're Different */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-4xl md:text-6xl font-bold text-center mb-20 text-white">
              The Haven Difference
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Video className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Virtual-First Convenience</h3>
                    <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                      Consult from home, work, or anywhere with an internet connection. Evening and weekend appointments available to fit your schedule.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Award className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Specialized Expertise</h3>
                    <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                      Travel medicine-trained clinicians who stay current with evolving health risks, vaccine recommendations, and international entry requirements.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Calendar className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Coordinated Care</h3>
                    <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                      We work with partner pharmacies and clinics across British Columbia to coordinate your vaccine appointments and prescriptions seamlessly.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-10 rounded-3xl border border-cyan-400/20 hover:shadow-2xl hover:shadow-cyan-400/20 transition-all duration-300">
                <div className="flex items-start gap-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg">
                    <Shield className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Transparent Pricing</h3>
                    <p className="font-sans text-base md:text-lg text-gray-200 leading-relaxed">
                      Clear consultation fees and published vaccine pricing—no hidden costs or surprises. You'll know exactly what to expect.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-cyan-400/20 via-blue-500/20 to-cyan-400/20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-400/30 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-heading text-4xl md:text-6xl font-bold mb-8 text-white leading-tight">
              Ready to Travel with Confidence?
            </h2>
            <p className="font-sans text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
              Book your virtual consultation today and receive personalized travel health guidance from our expert team.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button
                onClick={() => setIsBookingModalOpen(true)}
                className="w-full sm:w-auto px-10 py-5 bg-gradient-to-r from-cyan-400 to-blue-500 text-white font-heading font-bold rounded-2xl hover:shadow-2xl hover:shadow-cyan-400/50 transition-all text-lg hover:scale-105 active:scale-95"
              >
                Book Virtual Consult
              </button>
              <Link
                to="/vaccine-pricing"
                className="w-full sm:w-auto px-10 py-5 bg-transparent border-2 border-cyan-400 text-cyan-400 font-heading font-bold rounded-2xl hover:bg-cyan-400/10 transition-all text-lg hover:scale-105 active:scale-95 text-center"
              >
                View Vaccine Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
}
